import { useCallback, useState } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';

import DeleteIconBordered from '../../../../../../../components/svgs/DeleteIconBordered';
import { FBoxContainer } from '../../../../../../../components/baseV2';
import {
  DocumentPreviewDialog,
  QuestionnaireSectionLabel,
} from '../../../../../common';
import { ConfirmDialog, LIST_CONTAINER_SX, LIST_ROW_BORDER_SX } from '../../../../../../common';
import {
  REFERRAL_LIST_ROW_BG_SX,
  UPLOADED_DOCUMENT_ROW_BASE_SX,
  UPLOADED_DOCUMENT_TITLE_SX,
} from '../styles';

import type { KeyboardEvent, MouseEvent } from 'react';
import type { IOnboardingQuestionnaireDocument } from '../../../../types';
import type { UploadedDocumentPendingDelete, UploadedDocumentsSectionProps } from './types';

function activateOnEnterOrSpace(event: KeyboardEvent, handler: () => void) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handler();
  }
}

export default function UploadedDocumentsSection({
  documents,
  disabled,
  onDelete,
}: UploadedDocumentsSectionProps) {
  const [pendingDelete, setPendingDelete] = useState<UploadedDocumentPendingDelete | null>(null);
  const [previewingDocument, setPreviewingDocument] =
    useState<IOnboardingQuestionnaireDocument | null>(null);

  const handleRequestDelete = useCallback(
    (event: MouseEvent, document: IOnboardingQuestionnaireDocument) => {
      event.stopPropagation();
      setPendingDelete({ id: document.id, title: document.title });
    },
    [],
  );

  const handleCancelDelete = useCallback(() => setPendingDelete(null), []);

  const handleConfirmDelete = useCallback(async () => {
    if (pendingDelete == null) {
      return;
    }
    setPendingDelete(null);
    await onDelete(pendingDelete.id);
  }, [onDelete, pendingDelete]);

  const handleClosePreview = useCallback(() => setPreviewingDocument(null), []);

  return (
    <Stack gap={1}>
      <QuestionnaireSectionLabel variant="h6">Uploaded documents</QuestionnaireSectionLabel>
      <FBoxContainer sx={LIST_CONTAINER_SX}>
        <Stack>
          {documents.map((document, index) => (
            <Stack
              key={document.id}
              aria-label={`Preview ${document.title}`}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setPreviewingDocument(document)}
              onKeyDown={(event) =>
                activateOnEnterOrSpace(event, () => setPreviewingDocument(document))
              }
              role="button"
              tabIndex={0}
              sx={[
                UPLOADED_DOCUMENT_ROW_BASE_SX,
                LIST_ROW_BORDER_SX,
                REFERRAL_LIST_ROW_BG_SX[index % 2],
              ]}
            >
              <Typography title={document.title} sx={UPLOADED_DOCUMENT_TITLE_SX}>
                {document.title}
              </Typography>
              {!disabled && (
                <IconButton
                  aria-label={`Delete ${document.title}`}
                  onClick={(event) => handleRequestDelete(event, document)}
                  size="small"
                  sx={{ p: 0 }}
                >
                  <DeleteIconBordered width={36} height={36} />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      </FBoxContainer>

      <ConfirmDialog
        message={pendingDelete ? `You are about to delete "${pendingDelete.title}"` : ''}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        open={pendingDelete != null}
        title="Delete this document?"
      />

      <DocumentPreviewDialog
        documentFileType={previewingDocument?.file_type}
        documentPreviewUrl={previewingDocument?.document_url ?? ''}
        illustrationAlt={previewingDocument?.title ?? ''}
        onClose={handleClosePreview}
        open={previewingDocument != null}
      />
    </Stack>
  );
}
